import axios from 'axios';
import { refreshToken } from '../services/authServices';
import { AUTH_TOKEN_KEY, AUTH_REFRESH_TOKEN_KEY } from '../constants/constant';
import { getParsedItem, removeItem, setParsedItem } from '../utils/Storage';
import { logout } from '../redux/actions/authActions';
import store from '../redux/store';

let isRefreshing = false;
let refreshTokenPromise = null;

const restrictUrlsForRefreshToken = [
  '/api/v1/login/refresh',
  '/api/v1/login/brand',
];

export const createAxiosInstance = (baseURL) => {
  const refresh_token = getParsedItem(AUTH_REFRESH_TOKEN_KEY);

  const { dispatch } = store;

  const instance = axios.create({
    baseURL: baseURL,
  });

  instance.interceptors.request.use(
    async (config) => {
      const token = getParsedItem(AUTH_TOKEN_KEY);
      if (token) {
        config.headers['Authorization'] = `Bearer ${token}`;
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  instance.interceptors.response.use(
    (response) => {
      // If response status is OK, return response directly
      return response;
    },
    async (error) => {
      const originalRequest = error.config;

      // If response status is 401 (unauthorized) and original request was not a token refresh request
      if (
        error.response.status === 401 &&
        !originalRequest._retry &&
        refresh_token &&
        // originalRequest.url !== '/api/v1/login/refresh' &&
        // originalRequest.url !== '/api/v1/login/brand'
        !restrictUrlsForRefreshToken.includes(originalRequest.url)
      ) {
        originalRequest._retry = true; // Mark the request as retried

        if (!isRefreshing) {
          isRefreshing = true; // Set flag to indicate token refresh is in progress
          refreshTokenPromise = refreshTokenHandler(dispatch); // Start token refresh process
        }

        // Enqueue the failed request and return a promise to retry it after token refresh
        return enqueueRequest(originalRequest);
      }

      // If response status is not 401 or token refresh is not required, return the error
      return Promise.reject(error);
    }
  );

  return instance;
};

// Function to handle token refresh
const refreshTokenHandler = async (dispatch) => {
  try {
    const token = getParsedItem(AUTH_REFRESH_TOKEN_KEY);
    const response = await refreshToken({ token });
    const accessToken = response?.data?.access_token;
    setParsedItem(AUTH_TOKEN_KEY, accessToken);
    return accessToken;
  } catch (error) {
    console.error('Token refresh failed:', error);
    // Handle token refresh failure
    dispatch(logout());
    window.location.href = '/login';
    removeItem(AUTH_TOKEN_KEY);
    removeItem(AUTH_REFRESH_TOKEN_KEY);
    return Promise.reject(error);
  } finally {
    isRefreshing = false; // Reset flag after token refresh attempt
  }
};

// Function to enqueue failed requests
const enqueueRequest = (request) => {
  return new Promise((resolve, reject) => {
    refreshTokenPromise
      .then((accessToken) => {
        // Retry the original request with the new token
        request.headers['Authorization'] = `Bearer ${accessToken}`;
        resolve(axios(request));
      })
      .catch((error) => {
        reject(error);
      });
  });
};
